import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";

import { css } from "@compiled/react";
import { debounce } from "lodash";
import { useIntl } from "react-intl";

import SearchIcon from "@atlaskit/icon/core/search";
import { Flex, Stack, xcss } from "@atlaskit/primitives-next";
import Textfield from "@atlaskit/textfield";
import { token } from "@atlaskit/tokens";

import { Search } from "../types";

import { messages } from "./messages";

const searchIconContainerStyles = xcss({
    paddingInlineStart: "space.100"
});

const searchInputContainerStyles = css({
    "width": "200px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> div": {
        border: `1px solid ${token("color.border")}`
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> div input:not([data-compact])": {
        height: "30px"
    }
});

export const FastshiftTableSearch = ({
    search,
    id = "fastshift-table-search",
    testId = "fastshift-table-search"
}: {
    search: Search;
    id?: string;
    testId?: string;
}) => {
    const { formatMessage } = useIntl();

    const { onSetSearchTerm, searchTerm } = search;

    const [inputValue, setInputValue] = useState(searchTerm);

    useEffect(() => {
        setInputValue(searchTerm);
    }, [searchTerm]);

    const debouncedSetSearchTerm = useMemo(
        () => debounce((value: string) => onSetSearchTerm(value), 500),
        [onSetSearchTerm]
    );

    useEffect(() => {
        return () => {
            debouncedSetSearchTerm.cancel();
        };
    }, [debouncedSetSearchTerm]);

    const handleInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            setInputValue(value);
            debouncedSetSearchTerm(value);
        },
        [debouncedSetSearchTerm]
    );

    return (
        <Stack space="space.200">
            <div css={searchInputContainerStyles}>
                <Textfield
                    appearance="standard"
                    id={`${id}-input`}
                    testId={`${testId}-input`}
                    placeholder={formatMessage(messages.searchPlaceholder)}
                    onChange={handleInputChange}
                    value={inputValue}
                    elemBeforeInput={
                        <Flex
                            xcss={searchIconContainerStyles}
                            alignItems="center"
                        >
                            <SearchIcon
                                label={formatMessage(messages.searchLabel)}
                                size="medium"
                            />
                        </Flex>
                    }
                />
            </div>
        </Stack>
    );
};
